import { computePosition, autoUpdate, autoPlacement } from '@floating-ui/dom';

class TooltipUI extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.tooltipTrigger = this.querySelector('[data-tooltip-trigger]');
    this.tooltipContent = this.querySelector('[data-tooltip-content]');

    if (!this.tooltipTrigger || !this.tooltipContent) return;

    this.cleanup = null;
    this.hideTimeout = null;
    this.listeners = {
      showTooltip: this.showTooltip.bind(this),
      hideTooltip: this.hideTooltip.bind(this),
      scheduleHideTooltip: this.scheduleHideTooltip.bind(this),
      handleWindowClick: this.handleWindowClick.bind(this),
      // Prevent clicks/touches on tooltip trigger from toggling parent details/summary
      // Also show tooltip on mobile tap
      stopPropagationTrigger: (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        // Show tooltip on mobile tap
        if (e.type === 'touchstart' || e.type === 'click') {
          // Open parent details element if closed to prevent tooltip from being cut off
          this.ensureParentDetailsOpen();
          this.showTooltip();
        }
      },
      // Prevent clicks/touches on tooltip content from toggling parent details/summary
      stopPropagationContent: (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      },
    };

    window.addEventListener('click', this.listeners.handleWindowClick);

    [
      ['mouseenter', this.listeners.showTooltip],
      ['mouseleave', this.listeners.scheduleHideTooltip],
      ['focus', this.listeners.showTooltip],
      ['blur', this.listeners.hideTooltip],
    ].forEach(([event, listener]) => {
      this.tooltipTrigger.addEventListener(event, listener);
    });

    // Keep tooltip open when hovering over it
    [
      ['mouseenter', this.listeners.showTooltip],
      ['mouseleave', this.listeners.scheduleHideTooltip],
    ].forEach(([event, listener]) => {
      this.tooltipContent.addEventListener(event, listener);
    });

    [
      ['click', this.listeners.stopPropagationTrigger],
      ['touchstart', this.listeners.stopPropagationTrigger],
    ].forEach(([event, listener]) => {
      this.tooltipTrigger.addEventListener(event, listener);
    });

    [
      ['click', this.listeners.stopPropagationContent],
      ['touchstart', this.listeners.stopPropagationContent],
    ].forEach(([event, listener]) => {
      this.tooltipContent.addEventListener(event, listener);
    });
  }

  disconnectedCallback() {
    if (this.cleanup) {
      this.cleanup();
      this.cleanup = null;
    }

    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }

    if (this.tooltipTrigger && this.listeners) {
      [
        ['mouseenter', this.listeners.showTooltip],
        ['mouseleave', this.listeners.scheduleHideTooltip],
        ['focus', this.listeners.showTooltip],
        ['blur', this.listeners.hideTooltip],
        ['click', this.listeners.stopPropagationTrigger],
        ['touchstart', this.listeners.stopPropagationTrigger],
      ].forEach(([event, listener]) => {
        this.tooltipTrigger.removeEventListener(event, listener);
      });
    }

    if (this.tooltipContent && this.listeners) {
      [
        ['mouseenter', this.listeners.showTooltip],
        ['mouseleave', this.listeners.scheduleHideTooltip],
        ['click', this.listeners.stopPropagationContent],
        ['touchstart', this.listeners.stopPropagationContent],
      ].forEach(([event, listener]) => {
        this.tooltipContent.removeEventListener(event, listener);
      });
    }

    window.removeEventListener('click', this.listeners?.handleWindowClick);
  }

  update() {
    computePosition(this.tooltipTrigger, this.tooltipContent, {
      placement: 'top',
      middleware: [offset(10)],
    });
  }

  ensureParentDetailsOpen() {
    // Find the closest parent details element
    const parentDetails = this.closest('details');
    if (parentDetails && !parentDetails.open) {
      parentDetails.open = true;
    }
  }

  showTooltip() {
    // Cancel any pending hide timeout
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }

    if (this.cleanup) return; // Already showing

    this.tooltipContent.classList.remove('opacity-0', 'invisible');

    // Initial position update
    this.update();

    // Start auto-updating position
    this.cleanup = autoUpdate(this.tooltipTrigger, this.tooltipContent, () => {
      this.update();
    });
  }

  scheduleHideTooltip() {
    // Cancel any existing timeout
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }

    // Schedule hide with a small delay to allow moving between trigger and tooltip
    this.hideTimeout = setTimeout(() => {
      this.hideTooltip();
      this.hideTimeout = null;
    }, 100);
  }

  hideTooltip() {
    // Cancel any pending hide timeout
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }

    if (this.cleanup) {
      this.cleanup();
      this.cleanup = null;
    }
    this.tooltipContent.classList.add('opacity-0', 'invisible');
  }

  handleWindowClick(event) {
    // Don't hide if click is inside the tooltip element
    if (this.contains(event.target)) {
      return;
    }
    this.hideTooltip();
  }
}

if (!customElements.get('tooltip-ui')) {
  customElements.define('tooltip-ui', TooltipUI);
}